import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { IHttpClient } from '@micro-frontend-react/employee-experience/lib/IHttpClient';
import type { SagaIterator } from 'redux-saga';
import axios from 'axios';
import { all, call, cancelled, getContext, put, takeLatest } from 'redux-saga/effects';

import { PBTApiEndpoints, PBTMessages } from './PBTConstants';
import { PBTCrmCasesActionType } from '../Redux/PBT/CRMCases/action-types';
import { receiveCrmCases, sendCrmCasesError } from '../Redux/PBT/CRMCases/actions';
import { PBTCreateCaseActionType } from '../Redux/PBT/CreateCase/action-types';
import {
  createCaseRequest,
  createCaseSuccess,
  createCaseError,
  bulkCreateRequest,
  bulkCreateSuccess,
  bulkCreateError,
  excelUploadRequest,
  excelUploadSuccess,
  excelUploadError,
} from '../Redux/PBT/CreateCase/actions';
import { requestCrmCases } from '../Redux/PBT/CRMCases/actions';

function auth(authClient: IAuthClient) {
  return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

// Surfaces the backend's error message from either a resolved IHttpClientResult or a thrown
// Axios error. The API returns { error: { code, message } }; some endpoints use { errors: [...] }.
function extractApiErrorMessage(source: any, fallback: string): string {
  const data = source?.response?.data ?? source?.data ?? source;
  return data?.error?.message || data?.errors?.[0]?.message || data?.message || fallback;
}

export function getCrmCasesErrorMessage(source: any): string {
  const status = source?.response?.status ?? source?.status;
  return status === 401 || status === 403 ? PBTMessages.CRMAuthorizationError : PBTMessages.CRMError;
}

export function isCrmCasesAuthorizationError(source: any): boolean {
  const status = source?.response?.status ?? source?.status;
  return status === 401 || status === 403;
}

// ── CRM Incoming Cases saga ────────────────────────────────────────────────

export function* requestCrmCasesSagas(): SagaIterator {
  yield all([takeLatest(PBTCrmCasesActionType.REQUEST_CRM_CASES, fetchCrmCases)]);
}

export function* fetchCrmCases(action: ReturnType<typeof requestCrmCases>): SagaIterator {
  const httpClient: IHttpClient = yield getContext('httpClient');
  const authClient: IAuthClient = yield getContext('authClient');
  const cancelSource = axios.CancelToken.source();
  try {
    const token = yield call(auth, authClient);
    const searchString = action.payload.searchString.trim();
    const result = yield call([httpClient, httpClient.request], {
      url: PBTApiEndpoints.CRMIncoming(action.payload.pageNumber, action.payload.pageSize, searchString),
      method: 'GET',
      accessToken: token,
      cancelToken: cancelSource.token,
    });
    if (result && result.status === 200 && result.data?.data) {
      yield put(receiveCrmCases(result.data.data, searchString));
    } else {
      yield put(sendCrmCasesError(getCrmCasesErrorMessage(result), isCrmCasesAuthorizationError(result)));
    }
  } catch (error) {
    console.error('Failed to fetch CRM cases:', error);
    yield put(sendCrmCasesError(getCrmCasesErrorMessage(error), isCrmCasesAuthorizationError(error)));
  } finally {
    if (yield cancelled()) {
      cancelSource.cancel('Superseded by a newer CRM search request.');
    }
  }
}

// ── Create single PBT Case saga ────────────────────────────────────────────

export function* createCaseSagas(): SagaIterator {
  yield all([
    takeLatest(PBTCreateCaseActionType.CREATE_CASE_REQUEST, postCreateCase),
    takeLatest(PBTCreateCaseActionType.BULK_CREATE_REQUEST, postBulkCreate),
    takeLatest(PBTCreateCaseActionType.EXCEL_UPLOAD_REQUEST, postExcelUpload),
  ]);
}

function* postCreateCase(action: ReturnType<typeof createCaseRequest>): SagaIterator {
  const httpClient: IHttpClient = yield getContext('httpClient');
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token = yield call(auth, authClient);
    // Transform camelCase to PascalCase for backend
    const payload = {
      CrmCaseId: action.payload.crmCaseId,
      EngagementModel: action.payload.engagementModel,
      CrmOutcome: action.payload.crmOutcome,
      RehireEligibility: action.payload.rehireEligibility,
      HrcAlias: action.payload.hrcAlias,
      BackupHrcAlias: action.payload.backupHrcAlias,
      Notes: action.payload.notes,
    };
    const result = yield call([httpClient, httpClient.request], {
      url: PBTApiEndpoints.CreateCase,
      method: 'POST',
      accessToken: token,
      data: payload,
    });
    if (result && (result.status === 200 || result.status === 201) && result.data?.data) {
      yield put(createCaseSuccess(result.data.data));
      // Refresh CRM incoming list after successful creation
      yield put(requestCrmCases());
    } else {
      const msg = extractApiErrorMessage(result, PBTMessages.CreateError);
      yield put(createCaseError(msg));
    }
  } catch (error) {
    console.error('Failed to create PBT case:', error);
    yield put(createCaseError(extractApiErrorMessage(error, PBTMessages.CreateError)));
  }
}

// ── Bulk Create PBT Cases saga ─────────────────────────────────────────────

function* postBulkCreate(action: ReturnType<typeof bulkCreateRequest>): SagaIterator {
  const httpClient: IHttpClient = yield getContext('httpClient');
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token = yield call(auth, authClient);
    // Transform camelCase to PascalCase for backend
    const transformedCases = action.payload.cases.map((c) => ({
      CrmCaseId: c.crmCaseId,
      EngagementModel: c.engagementModel,
      CrmOutcome: c.crmOutcome,
      RehireEligibility: c.rehireEligibility,
      HrcAlias: c.hrcAlias,
      BackupHrcAlias: c.backupHrcAlias,
      Notes: c.notes,
    }));
    const result = yield call([httpClient, httpClient.request], {
      url: PBTApiEndpoints.BulkCreateCase,
      method: 'POST',
      accessToken: token,
      data: { Cases: transformedCases },
    });
    if (result && (result.status === 200 || result.status === 207) && result.data?.data) {
      const { successCount, failureCount, blockedCount, warningCount, errors } = result.data.data;
      yield put(
        bulkCreateSuccess(successCount ?? 0, failureCount ?? 0, blockedCount ?? 0, warningCount ?? 0, errors ?? [])
      );
      yield put(requestCrmCases());
    } else {
      yield put(bulkCreateError(PBTMessages.BulkCreateError));
    }
  } catch (error) {
    console.error('Failed to bulk create PBT cases:', error);
    yield put(bulkCreateError(PBTMessages.BulkCreateError));
  }
}

// ── Excel Bulk Upload saga ─────────────────────────────────────────────────

function* postExcelUpload(action: ReturnType<typeof excelUploadRequest>): SagaIterator {
  const httpClient: IHttpClient = yield getContext('httpClient');
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token = yield call(auth, authClient);
    const formData = new FormData();
    // Field name must match the controller parameter ("file").
    formData.append('file', action.payload.file, action.payload.file.name);
    const result = yield call([httpClient, httpClient.request], {
      url: PBTApiEndpoints.ExcelBulkUpload,
      method: 'POST',
      accessToken: token,
      data: formData,
    });
    if (result && (result.status === 200 || result.status === 207) && result.data?.data) {
      yield put(excelUploadSuccess(result.data.data));
      // Refresh CRM incoming list so newly created cases drop out of the "available" set.
      yield put(requestCrmCases());
    } else {
      // A 400 with a structural rejection message surfaces the server's reason where available.
      const msg = result?.data?.errors?.[0]?.message || result?.data?.message || PBTMessages.ExcelUploadError;
      yield put(excelUploadError(msg));
    }
  } catch (error) {
    console.error('Failed to upload Excel bulk file:', error);
    yield put(excelUploadError(PBTMessages.ExcelUploadError));
  }
}
